import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Edit2, Copy, Check, MessageCircle, FileText, Save, Play, Send } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { format, differenceInMonths } from 'date-fns';
import { collection, doc, getDocs, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

const SCRIPT_STORAGE_KEY = 'recallready_selected_script';

// ===== EXPORTED FUNCTION =====
export function applyScriptToPatient(script, patient, practiceName, personalizedLine) {
  if (!script || !patient) return '';
  
  const lastVisitDate = patient.lastVisit ? format(new Date(patient.lastVisit), 'dd MMM yyyy') : 'N/A';
  const monthsSinceLastVisit = patient.lastVisit ? differenceInMonths(new Date(), new Date(patient.lastVisit)) : 'N/A';
  
  let message = script.template;
  message = message.replace(/{firstName}/g, patient.firstName || 'Patient');
  message = message.replace(/{practiceName}/g, practiceName || 'Your Practice');
  message = message.replace(/{treatmentType}/g, patient.treatmentType || 'dental');
  message = message.replace(/{lastVisitDate}/g, lastVisitDate);
  message = message.replace(/{monthsSinceLastVisit}/g, monthsSinceLastVisit);
  message = message.replace(/{personalizedLine}/g, personalizedLine || `We've been thinking about you and wanted to check in.`);
  
  return message;
}

// Demo scripts
const DEMO_SCRIPTS = [
  {
    id: 'demo-1',
    name: 'Warm Check-in',
    category: 'Relationship',
    template: `Hi {firstName},

This is {practiceName} reaching out. I noticed it's been {monthsSinceLastVisit} months since we last saw you, and I wanted to personally check in.

{personalizedLine}

We'd love to see you for your {treatmentType} follow-up. How does next week look for you?

Looking forward to seeing you soon,
The {practiceName} Team`,
    description: 'Warm, personal check-in that builds relationship and trust',
    isDemo: true,
  },
  {
    id: 'demo-2',
    name: 'Value-First Reminder',
    category: 'Value',
    template: `Hi {firstName},

At {practiceName}, we believe in proactive dental care. It's been {monthsSinceLastVisit} months since your last visit, and we want to make sure your smile stays healthy.

{personalizedLine}

Your {treatmentType} treatment is due for review. Did you know that regular check-ups can prevent 90% of dental issues?

Book a time that works for you, and we'll make sure you're taken care of.

Warm regards,
The {practiceName} Team`,
    description: 'Highlights the value of regular care and prevention',
    isDemo: true,
  },
  {
    id: 'demo-3',
    name: 'Personalized Care',
    category: 'Personalization',
    template: `Hi {firstName},

{personalizedLine}

This is {practiceName}, and we remember how important your {treatmentType} treatment was to you. We've been thinking about you and wanted to check in.

It's been {monthsSinceLastVisit} months since your last visit, and we'd love to see how everything is going.

Reply to this message and we'll find a time that works perfectly for you.

Your friends at {practiceName}`,
    description: 'High emotional connection and personalized care',
    isDemo: true,
  },
  {
    id: 'demo-4',
    name: 'Educational & Helpful',
    category: 'Education',
    template: `Hi {firstName},

Did you know that regular dental visits can detect early signs of health issues beyond your teeth?

At {practiceName}, we're committed to your overall health. It's been {monthsSinceLastVisit} months since we last saw you, and your {treatmentType} follow-up is important.

{personalizedLine}

We've reserved some time slots for our valued patients like you. When works best for you?

Looking forward to seeing you,
The {practiceName} Team`,
    description: 'Educates while gently prompting action',
    isDemo: true,
  },
  {
    id: 'demo-5',
    name: 'Short & Direct',
    category: 'Direct',
    template: `Hi {firstName}, it's {practiceName}!

{personalizedLine}

It's been {monthsSinceLastVisit} months — time for your {treatmentType} check-up.

We have availability this week. What day works best for you?

Reply or call us to book! 😊`,
    description: 'Brief, friendly, and action-oriented',
    isDemo: true,
  },
];

// ===== STYLES - Defined BEFORE the component =====
const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  zIndex: 1000,
  display: 'flex',
  justifyContent: 'flex-end',
};

const panelStyle = {
  background: '#FFFFFF',
  width: 620,
  maxWidth: '100vw',
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '20px 28px',
  borderBottom: '1px solid #E8ECE9',
};

const headerTitleStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  fontSize: 18,
  fontWeight: 600,
  color: '#1A3A3A',
  fontFamily: "'Inter', sans-serif",
};

const headerSubStyle = {
  fontSize: 13,
  color: '#7A9A92',
  marginTop: 4,
  fontFamily: "'Inter', sans-serif",
};

const closeBtnStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: '#7A9A92',
  padding: 4,
  display: 'flex',
  alignItems: 'center',
};

const listContentStyle = {
  padding: '24px 28px',
  flex: 1,
  overflowY: 'auto',
};

const filtersStyle = {
  display: 'flex',
  gap: 10,
  marginBottom: 16,
};

const searchInputStyle = {
  flex: 1,
  padding: '8px 12px',
  border: '1px solid #DDDEDC',
  borderRadius: 8,
  fontSize: 13,
  fontFamily: "'Inter', sans-serif",
  color: '#1A3A3A',
  outline: 'none',
};

const filterSelectStyle = {
  padding: '8px 12px',
  border: '1px solid #DDDEDC',
  borderRadius: 8,
  fontSize: 13,
  fontFamily: "'Inter', sans-serif",
  color: '#1A3A3A',
  background: '#FFFFFF',
  outline: 'none',
  cursor: 'pointer',
};

const createBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '10px 16px',
  background: '#2D9B8A',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
  marginBottom: 20,
};

const scriptListStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const scriptCardStyle = {
  border: '1px solid #E8ECE9',
  borderRadius: 8,
  padding: '16px 20px',
  transition: 'all 0.2s',
};

const scriptCardHeader = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: 8,
};

const scriptNameStyle = {
  fontSize: 15,
  fontWeight: 600,
  color: '#1A3A3A',
  fontFamily: "'Inter', sans-serif",
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const demoBadgeStyle = {
  fontSize: 10,
  fontWeight: 600,
  color: '#2D9B8A',
  background: 'rgba(45, 155, 138, 0.1)',
  padding: '2px 8px',
  borderRadius: 4,
  textTransform: 'uppercase',
};

const scriptCategoryStyle = {
  fontSize: 11,
  color: '#7A9A92',
  fontFamily: "'Inter', sans-serif",
  marginTop: 2,
};

const scriptActionsStyle = {
  display: 'flex',
  gap: 6,
  alignItems: 'center',
};

const useBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 12px',
  background: '#2D9B8A',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
};

const actionIconStyle = {
  padding: '4px 6px',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: '#7A9A92',
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
};

const scriptDescriptionStyle = {
  fontSize: 12,
  color: '#5A7A72',
  fontFamily: "'Inter', sans-serif",
  marginBottom: 6,
  fontStyle: 'italic',
};

const scriptTemplatePreviewStyle = {
  fontSize: 12,
  color: '#7A9A92',
  fontFamily: "'JetBrains Mono', monospace",
  background: '#F8FAF9',
  padding: '8px 12px',
  borderRadius: 4,
  border: '1px solid #E8ECE9',
  whiteSpace: 'pre-wrap',
  lineHeight: 1.5,
};

const editorContentStyle = {
  padding: '24px 28px',
  flex: 1,
  overflowY: 'auto',
};

const editorHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  marginBottom: 20,
};

const backBtnStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: '#2D9B8A',
  fontSize: 14,
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
  padding: 0,
};

const editorTitleStyle = {
  fontSize: 16,
  fontWeight: 600,
  color: '#1A3A3A',
  fontFamily: "'Inter', sans-serif",
};

const editorFormStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

const fieldStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const labelStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: '#4A5A52',
  fontFamily: "'Inter', sans-serif",
};

const inputStyle = {
  padding: '10px 14px',
  border: '1px solid #DDDEDC',
  borderRadius: 8,
  fontSize: 13,
  fontFamily: "'Inter', sans-serif",
  color: '#1A3A3A',
  outline: 'none',
};

const selectStyle = {
  padding: '10px 14px',
  border: '1px solid #DDDEDC',
  borderRadius: 8,
  fontSize: 13,
  fontFamily: "'Inter', sans-serif",
  color: '#1A3A3A',
  background: '#FFFFFF',
  outline: 'none',
  cursor: 'pointer',
};

const templateTextareaStyle = {
  padding: '12px 14px',
  border: '1px solid #DDDEDC',
  borderRadius: 8,
  fontSize: 13,
  fontFamily: "'JetBrains Mono', monospace",
  color: '#1A3A3A',
  outline: 'none',
  resize: 'vertical',
  minHeight: 180,
  lineHeight: 1.6,
};

const variableHelpStyle = {
  background: '#F8FAF9',
  border: '1px solid #E8ECE9',
  borderRadius: 8,
  padding: '12px 16px',
  marginTop: 8,
};

const variableHelpTitle = {
  fontSize: 11,
  fontWeight: 600,
  color: '#4A5A52',
  marginBottom: 8,
  fontFamily: "'Inter', sans-serif",
};

const variableHelpGrid = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  gap: '4px 12px',
  fontSize: 12,
  fontFamily: "'JetBrains Mono', monospace",
};

const variableTag = {
  color: '#2D9B8A',
  fontWeight: 500,
  background: 'rgba(45, 155, 138, 0.08)',
  padding: '2px 8px',
  borderRadius: 4,
};

const variableDesc = {
  color: '#5A7A72',
  fontSize: 11,
};

const editorActionsStyle = {
  display: 'flex',
  gap: 10,
  marginTop: 8,
};

const cancelBtnStyle = {
  flex: 1,
  padding: '11px 0',
  background: '#FFFFFF',
  color: '#1A3A3A',
  border: '1px solid #DDDEDC',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
};

const saveBtnStyle = {
  flex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '11px 0',
  background: '#2D9B8A',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
};

// ===== Apply Modal Styles =====
const applyOverlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  zIndex: 1100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const applyModalStyle = {
  background: '#FFFFFF',
  borderRadius: 16,
  padding: '28px 32px',
  width: '100%',
  maxWidth: 580,
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 8px 48px rgba(0,0,0,0.2)',
};

const applyHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 20,
};

const applyTitleStyle = {
  fontSize: 18,
  fontWeight: 600,
  color: '#1A3A3A',
  fontFamily: "'Inter', sans-serif",
};

const applyContentStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};

const personalizedTextareaStyle = {
  padding: '12px 14px',
  border: '1px solid #DDDEDC',
  borderRadius: 8,
  fontSize: 13,
  fontFamily: "'Inter', sans-serif",
  color: '#1A3A3A',
  outline: 'none',
  resize: 'vertical',
  minHeight: 60,
};

const suggestionStyle = {
  fontSize: 11,
  color: '#7A9A92',
  fontFamily: "'Inter', sans-serif",
  fontStyle: 'italic',
  marginTop: 4,
};

const applyOptionsStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
  padding: '12px 0',
  borderTop: '1px solid #E8ECE9',
  borderBottom: '1px solid #E8ECE9',
};

const applyAllBtnStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  width: '100%',
  padding: '10px 0',
  background: '#2D9B8A',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 14,
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
};

const applyOrStyle = {
  fontSize: 12,
  color: '#B5C8C4',
  fontFamily: "'Inter', sans-serif",
};

const applyPatientSelectStyle = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const applyPatientLabel = {
  fontSize: 12,
  color: '#4A5A52',
  fontFamily: "'Inter', sans-serif",
};

const patientSelectStyle = {
  padding: '10px 14px',
  border: '1px solid #DDDEDC',
  borderRadius: 8,
  fontSize: 13,
  fontFamily: "'Inter', sans-serif",
  color: '#1A3A3A',
  background: '#FFFFFF',
  outline: 'none',
  cursor: 'pointer',
};

const applyPreviewStyle = {
  background: '#F8FAF9',
  border: '1px solid #E8ECE9',
  borderRadius: 8,
  padding: '12px 16px',
};

const applyPreviewTitle = {
  fontSize: 11,
  fontWeight: 600,
  color: '#4A5A52',
  marginBottom: 8,
  fontFamily: "'Inter', sans-serif",
};

const applyPreviewMessage = {
  fontSize: 13,
  color: '#1A3A3A',
  fontFamily: "'JetBrains Mono', monospace",
  whiteSpace: 'pre-wrap',
  lineHeight: 1.6,
};

const applyFooterStyle = {
  display: 'flex',
  gap: 10,
  marginTop: 20,
  paddingTop: 16,
  borderTop: '1px solid #E8ECE9',
};

const applyBtnStyle = {
  flex: 2,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '11px 0',
  background: '#2D9B8A',
  color: '#FFFFFF',
  border: 'none',
  borderRadius: 8,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 500,
  fontFamily: "'Inter', sans-serif",
};

// ===== COMPONENT =====
export default function ScriptManager({ onClose, onUseScript, practiceName }) {
  const { currentUser, isDemo } = useAuth();
  const { addToast } = useToast();
  const [scripts, setScripts] = useState([]);
  const [selectedScript, setSelectedScript] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingScript, setEditingScript] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [personalizedLine, setPersonalizedLine] = useState('');
  const [applyToPatient, setApplyToPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  const categories = ['all', 'Relationship', 'Value', 'Personalization', 'Education', 'Direct', 'Custom'];

  // Load scripts from Firebase on mount
  useEffect(() => {
    loadScripts();
  }, []);

  async function loadScripts() {
    setLoading(true);
    try {
      if (isDemo) {
        const custom = loadCustomScriptsLocal();
        setScripts([...DEMO_SCRIPTS, ...custom]);
      } else if (currentUser) {
        const scriptsRef = collection(db, 'practices', currentUser.uid, 'scripts');
        const snapshot = await getDocs(scriptsRef);
        const customScripts = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setScripts([...DEMO_SCRIPTS, ...customScripts]);
      }
    } catch (error) {
      console.error('Error loading scripts:', error);
      addToast('Error loading scripts', 'error');
      const custom = loadCustomScriptsLocal();
      setScripts([...DEMO_SCRIPTS, ...custom]);
    }
    setLoading(false);
  }

  function loadCustomScriptsLocal() {
    try {
      const raw = localStorage.getItem('recallready_custom_scripts');
      if (raw) return JSON.parse(raw);
    } catch (e) { /* ignore */ }
    return [];
  }

  function saveCustomScriptsLocal(scripts) {
    localStorage.setItem('recallready_custom_scripts', JSON.stringify(scripts));
  }

  async function saveScriptToFirebase(script) {
    if (isDemo || !currentUser) {
      const customScripts = loadCustomScriptsLocal();
      const existingIndex = customScripts.findIndex(s => s.id === script.id);
      if (existingIndex >= 0) {
        customScripts[existingIndex] = script;
      } else {
        customScripts.push(script);
      }
      saveCustomScriptsLocal(customScripts);
      return;
    }

    const scriptRef = doc(db, 'practices', currentUser.uid, 'scripts', script.id);
    await setDoc(scriptRef, {
      ...script,
      userId: currentUser.uid,
      updatedAt: new Date().toISOString(),
      createdAt: script.createdAt || new Date().toISOString(),
    });
  }

  async function deleteScriptFromFirebase(scriptId) {
    if (isDemo || !currentUser) {
      const customScripts = loadCustomScriptsLocal();
      const updated = customScripts.filter(s => s.id !== scriptId);
      saveCustomScriptsLocal(updated);
      return;
    }

    const scriptRef = doc(db, 'practices', currentUser.uid, 'scripts', scriptId);
    await deleteDoc(scriptRef);
  }

  async function handleCreateScript() {
    setEditingScript({
      id: `custom-${Date.now()}`,
      name: '',
      category: 'Custom',
      template: 'Hi {firstName},\n\n{personalizedLine}\n\nThis is {practiceName} reaching out.\n\nLast visit: {lastVisitDate}\nMonths since: {monthsSinceLastVisit}\nTreatment: {treatmentType}',
      description: '',
      isDemo: false,
      createdAt: new Date().toISOString(),
    });
    setShowEditor(true);
  }

  function handleEditScript(script) {
    setEditingScript({ ...script });
    setShowEditor(true);
  }

  async function handleDeleteScript(scriptId) {
    try {
      await deleteScriptFromFirebase(scriptId);
      await loadScripts();
      addToast('Script deleted', 'info');
    } catch (error) {
      console.error('Error deleting script:', error);
      addToast('Failed to delete script', 'error');
    }
  }

  async function handleSaveScript() {
    if (!editingScript.name.trim()) {
      addToast('Please enter a script name', 'error');
      return;
    }
    if (!editingScript.template.trim()) {
      addToast('Please enter a message template', 'error');
      return;
    }

    try {
      await saveScriptToFirebase(editingScript);
      await loadScripts();
      setShowEditor(false);
      setEditingScript(null);
      addToast(editingScript.id?.startsWith('custom-') ? 'Script updated' : 'Script created', 'success');
    } catch (error) {
      console.error('Error saving script:', error);
      addToast('Failed to save script', 'error');
    }
  }

  function handleUseScript(script) {
    setSelectedScript(script);
    setShowApplyModal(true);
  }

  async function handleApplyScript() {
    if (!selectedScript) return;
    
    localStorage.setItem('recallready_selected_script', JSON.stringify(selectedScript));
    
    if (currentUser && !isDemo) {
      try {
        const settingsRef = doc(db, 'practices', currentUser.uid, 'settings', 'preferences');
        await setDoc(settingsRef, {
          selectedScriptId: selectedScript.id,
          selectedScript: selectedScript,
          updatedAt: new Date().toISOString(),
        }, { merge: true });
      } catch (error) {
        console.error('Error saving selected script:', error);
      }
    }

    if (onUseScript) {
      onUseScript({
        script: selectedScript,
        applyToAll: true,
      });
    }
    
    setShowApplyModal(false);
    onClose();
  }

  function handleDuplicateScript(script) {
    const newScript = {
      ...script,
      id: `custom-${Date.now()}`,
      name: `${script.name} (Copy)`,
      isDemo: false,
      createdAt: new Date().toISOString(),
    };
    setEditingScript(newScript);
    setShowEditor(true);
  }

  function getVariableHelp() {
    return (
      <div style={variableHelpStyle}>
        <div style={variableHelpTitle}>Available Variables:</div>
        <div style={variableHelpGrid}>
          <span style={variableTag}>{'{firstName}'}</span>
          <span style={variableDesc}>Patient's first name</span>
          <span style={variableTag}>{'{practiceName}'}</span>
          <span style={variableDesc}>Your practice name</span>
          <span style={variableTag}>{'{treatmentType}'}</span>
          <span style={variableDesc}>Treatment type (e.g., Routine cleaning)</span>
          <span style={variableTag}>{'{lastVisitDate}'}</span>
          <span style={variableDesc}>Last visit date</span>
          <span style={variableTag}>{'{monthsSinceLastVisit}'}</span>
          <span style={variableDesc}>Months since last visit</span>
          <span style={variableTag}>{'{personalizedLine}'}</span>
          <span style={variableDesc}>✨ Your custom personalization line</span>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={overlayStyle}>
        <div style={panelStyle}>
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <div style={{ fontSize: 14, color: '#7A9A92' }}>Loading scripts...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={panelStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <div>
            <div style={headerTitleStyle}>
              <FileText size={20} />
              My Scripts
            </div>
            <div style={headerSubStyle}>Manage your message templates</div>
          </div>
          <button onClick={onClose} style={closeBtnStyle}>
            <X size={18} />
          </button>
        </div>

        {!showEditor ? (
          // Script List View
          <div style={listContentStyle}>
            {/* Filters */}
            <div style={filtersStyle}>
              <input
                style={searchInputStyle}
                placeholder="Search scripts..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <select
                style={filterSelectStyle}
                value={categoryFilter}
                onChange={e => setCategoryFilter(e.target.value)}
              >
                {categories.map(c => (
                  <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                ))}
              </select>
            </div>

            {/* Create Button */}
            <button onClick={handleCreateScript} style={createBtnStyle}>
              <Plus size={16} />
              Create New Script
            </button>

            {/* Script Cards */}
            <div style={scriptListStyle}>
              {scripts.filter(s => {
                const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  s.description.toLowerCase().includes(searchTerm.toLowerCase());
                const matchesCategory = categoryFilter === 'all' || s.category === categoryFilter;
                return matchesSearch && matchesCategory;
              }).map(script => (
                <div key={script.id} style={scriptCardStyle}>
                  <div style={scriptCardHeader}>
                    <div>
                      <div style={scriptNameStyle}>
                        {script.name}
                        {script.isDemo && <span style={demoBadgeStyle}>Demo</span>}
                      </div>
                      <div style={scriptCategoryStyle}>{script.category}</div>
                    </div>
                    <div style={scriptActionsStyle}>
                      <button
                        onClick={() => handleUseScript(script)}
                        style={useBtnStyle}
                        title="Use this script"
                      >
                        <Send size={14} />
                        Use
                      </button>
                      {!script.isDemo && (
                        <>
                          <button
                            onClick={() => handleEditScript(script)}
                            style={actionIconStyle}
                            title="Edit script"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteScript(script.id)}
                            style={{ ...actionIconStyle, color: '#C0392B' }}
                            title="Delete script"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDuplicateScript(script)}
                        style={actionIconStyle}
                        title="Duplicate script"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  </div>
                  <div style={scriptDescriptionStyle}>{script.description}</div>
                  <div style={scriptTemplatePreviewStyle}>
                    {script.template.slice(0, 120)}
                    {script.template.length > 120 && '...'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          // Script Editor View
          <div style={editorContentStyle}>
            <div style={editorHeaderStyle}>
              <button onClick={() => setShowEditor(false)} style={backBtnStyle}>
                ← Back
              </button>
              <span style={editorTitleStyle}>
                {editingScript?.id?.startsWith('custom-') ? 'Edit Script' : 'Create Script'}
              </span>
            </div>

            <div style={editorFormStyle}>
              <div style={fieldStyle}>
                <label style={labelStyle}>Script Name *</label>
                <input
                  style={inputStyle}
                  value={editingScript?.name || ''}
                  onChange={e => setEditingScript({ ...editingScript, name: e.target.value })}
                  placeholder="e.g., Warm Check-in"
                />
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Category</label>
                <select
                  style={selectStyle}
                  value={editingScript?.category || 'Custom'}
                  onChange={e => setEditingScript({ ...editingScript, category: e.target.value })}
                >
                  <option value="Relationship">Relationship</option>
                  <option value="Value">Value</option>
                  <option value="Personalization">Personalization</option>
                  <option value="Education">Education</option>
                  <option value="Direct">Direct</option>
                  <option value="Custom">Custom</option>
                </select>
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Description</label>
                <input
                  style={inputStyle}
                  value={editingScript?.description || ''}
                  onChange={e => setEditingScript({ ...editingScript, description: e.target.value })}
                  placeholder="Brief description of when to use this script"
                />
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Message Template *</label>
                <textarea
                  style={templateTextareaStyle}
                  value={editingScript?.template || ''}
                  onChange={e => setEditingScript({ ...editingScript, template: e.target.value })}
                  placeholder="Write your message template here..."
                />
                {getVariableHelp()}
              </div>

              <div style={editorActionsStyle}>
                <button onClick={() => setShowEditor(false)} style={cancelBtnStyle}>
                  Cancel
                </button>
                <button onClick={handleSaveScript} style={saveBtnStyle}>
                  <Save size={14} />
                  Save Script
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Apply Script Modal */}
      {showApplyModal && (
        <div style={applyOverlayStyle} onClick={e => e.target === e.currentTarget && setShowApplyModal(false)}>
          <div style={applyModalStyle}>
            <div style={applyHeaderStyle}>
              <span style={applyTitleStyle}>Apply Script: {selectedScript?.name}</span>
              <button onClick={() => setShowApplyModal(false)} style={closeBtnStyle}>
                <X size={18} />
              </button>
            </div>
            
            <div style={applyContentStyle}>
              <div style={applyOptionsStyle}>
                <button 
                  onClick={handleApplyScript}
                  style={applyAllBtnStyle}
                >
                  <Send size={16} />
                  Apply to All Patients
                </button>
              </div>

              <div style={applyPreviewStyle}>
                <div style={applyPreviewTitle}>Preview:</div>
                <div style={applyPreviewMessage}>
                  {selectedScript && applyScriptToPatient(
                    selectedScript,
                    { firstName: 'Patient', treatmentType: 'Routine cleaning', lastVisit: new Date().toISOString() },
                    practiceName,
                    'Your personalized line here'
                  )}
                </div>
              </div>
            </div>

            <div style={applyFooterStyle}>
              <button onClick={() => setShowApplyModal(false)} style={cancelBtnStyle}>
                Cancel
              </button>
              <button onClick={handleApplyScript} style={applyBtnStyle}>
                <Send size={14} />
                Apply Script
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}